import { LoopableRegion, PPQN, RegionCollection } from "@opendaw/lib-dsp";
import { Arrays, Notifier, Option, safeExecute, Terminable, Terminator, UUID } from "@opendaw/lib-std";
import { Propagation } from "@opendaw/lib-box";
import { TrackBoxAdapter } from "../TrackBoxAdapter";
import { ValueEventCollectionBoxAdapter } from "../collection/ValueEventCollectionBoxAdapter";
import { ValueEventCollectionBox, ValueRegionBox } from "@opendaw/studio-boxes";
export class ValueRegionBoxAdapter {
    type = "value-region";
    #terminator = new Terminator();
    #context;
    #box;
    #changeNotifier;
    #isSelected;
    #isConstructing; // Prevents stack overflow due to infinite adapter queries
    #eventCollectionSubscription = Terminable.Empty;
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
        this.#isConstructing = true;
        this.#changeNotifier = new Notifier();
        this.#isSelected = false;
        this.#terminator.own(this.#box.pointerHub.subscribe({
            onAdded: () => this.#dispatchChange(),
            onRemoved: () => this.#dispatchChange()
        }));
        this.#terminator.own(this.#box.subscribe(Propagation.Children, (update) => {
            if (this.trackBoxAdapter.isEmpty()) {
                return;
            }
            if (update.type === "primitive" || update.type === "pointer") {
                const track = this.trackBoxAdapter.unwrap();
                if (this.#box.position.address.equals(update.address)) {
                    track.regions.onIndexingChanged();
                    this.#dispatchChange();
                }
                else {
                    this.#dispatchChange();
                }
            }
        }));
        this.#terminator.own(this.#box.events.catchupAndSubscribe(({ targetVertex }) => {
            this.#eventCollectionSubscription.terminate();
            this.#eventCollectionSubscription = targetVertex.match({
                none: () => Terminable.Empty,
                some: ({ box }) => this.#context.boxAdapters
                    .adapterFor(box, ValueEventCollectionBoxAdapter)
                    .subscribeChange(() => this.#dispatchChange())
            });
            this.#dispatchChange();
        }));
        this.#isConstructing = false;
    }
    valueAt(position, fallback) {
        const content = this.optCollection;
        return content.isEmpty() ? fallback : content.unwrap().valueAt(LoopableRegion.globalToLocal(this, position), fallback);
    }
    incomingValue(fallback) { return this.valueAt(this.position, fallback); }
    outgoingValue(fallback) {
        const optContent = this.optCollection;
        if (optContent.isEmpty()) {
            return fallback;
        }
        const content = optContent.unwrap();
        const endsOnLoopPass = (this.complete - this.offset) % this.loopDuration === 0;
        return endsOnLoopPass
            ? content.valueAt(this.loopDuration, fallback)
            : content.valueAt(LoopableRegion.globalToLocal(this, this.complete), fallback);
    }
    subscribeChange(observer) { return this.#changeNotifier.subscribe(observer); }
    accept(visitor) {
        return safeExecute(visitor.visitValueRegionBoxAdapter, this);
    }
    onSelected() {
        this.#isSelected = true;
        this.#dispatchChange();
    }
    onDeselected() {
        this.#isSelected = false;
        this.#dispatchChange();
    }
    get isSelected() { return this.#isSelected; }
    onValuesPropertyChanged() { this.#dispatchChange(); }
    onValuesSortingChanged() { this.onValuesPropertyChanged(); }
    terminate() {
        this.#eventCollectionSubscription.terminate();
        this.#terminator.terminate();
    }
    get box() { return this.#box; }
    get uuid() { return this.#box.address.uuid; }
    get address() { return this.#box.address; }
    get position() { return this.#box.position.getValue(); }
    get duration() { return this.#box.duration.getValue(); }
    get loopOffset() { return this.#box.loopOffset.getValue(); }
    get loopDuration() { return this.#box.loopDuration.getValue(); }
    get offset() { return this.position - this.loopOffset; }
    get complete() { return this.position + this.duration; }
    get mute() { return this.#box.mute.getValue(); }
    get hue() { return this.#box.hue.getValue(); }
    get hasCollection() { return !this.optCollection.isEmpty(); }
    get events() {
        return this.optCollection.map(collection => collection.events);
    }
    get optCollection() {
        return this.#box.events.targetVertex
            .map(vertex => this.#context.boxAdapters.adapterFor(vertex.box, ValueEventCollectionBoxAdapter));
    }
    get label() { return this.#box.label.getValue(); }
    get trackBoxAdapter() {
        if (this.#isConstructing) {
            return Option.None;
        }
        return this.#box.regions.targetVertex
            .map(vertex => this.#context.boxAdapters.adapterFor(vertex.box, TrackBoxAdapter));
    }
    get isMirrowed() { return this.optCollection.mapOr(adapter => adapter.numOwners > 1, false); }
    get canMirror() { return true; }
    copyTo(params) {
        const eventCollection = this.optCollection.unwrap("Cannot make copy without event-collection");
        const eventTarget = params?.consolidate === true
            ? eventCollection.copy().box.owners
            : eventCollection.box.owners;
        return this.#context.boxAdapters.adapterFor(ValueRegionBox.create(this.#context.boxGraph, UUID.generate(), box => {
            box.position.setValue(params?.position ?? this.position);
            box.duration.setValue(params?.duration ?? this.duration);
            box.loopOffset.setValue(params?.loopOffset ?? this.loopOffset);
            box.loopDuration.setValue(params?.loopDuration ?? this.loopDuration);
            box.hue.setValue(this.hue);
            box.label.setValue(this.label);
            box.mute.setValue(this.mute);
            box.events.refer(eventTarget);
            box.regions.refer(params?.track ?? this.#box.regions.targetVertex.unwrap());
        }), ValueRegionBoxAdapter);
    }
    consolidate() {
        if (!this.isMirrowed) {
            return;
        }
        this.events.ifSome(events => {
            const graph = this.#context.boxGraph;
            const collectionBox = ValueEventCollectionBox.create(graph, UUID.generate());
            events.asArray().forEach(adapter => adapter.copyTo({ events: collectionBox.events }));
            this.#box.events.refer(collectionBox.owners);
        });
    }
    canFlatten(_regions) {
        return false;
        /*return regions.length > 0 && Arrays.satisfy(regions, (a, b) => a.trackAdapter.contains(b.trackAdapter.unwrap()))
                && regions.every(region => region.isSelected && region instanceof ValueRegionBoxAdapter)*/
    }
    flatten(regions) {
        if (!this.canFlatten(regions)) {
            return Option.None;
        }
        const graph = this.#context.boxGraph;
        const sorted = regions.toSorted(RegionCollection.Comparator);
        const first = Arrays.getFirst(sorted, "Internal error (no first)");
        const last = Arrays.getLast(sorted, "Internal error (no last)");
        const rangeMin = first.position;
        const rangeMax = last.position + last.duration;
        const trackBoxAdapter = first.trackBoxAdapter.unwrap();
        const collectionBox = ValueEventCollectionBox.create(graph, UUID.generate());
        const overlapping = Array.from(trackBoxAdapter.regions.collection.iterateRange(rangeMin, rangeMax));
        overlapping
            .filter(region => region.isSelected)
            .forEach(anyRegion => {
            const region = anyRegion; // we made that sure in canFlatten
            for (const { resultStart, resultEnd, rawStart } of LoopableRegion.locateLoops(region, region.position, region.complete)) {
                const searchStart = Math.floor(resultStart - rawStart);
                const searchEnd = Math.floor(resultEnd - rawStart);
                for (const _event of region.events.unwrap().iterateRange(searchStart, searchEnd)) {
                    // TODO Flatten
                }
            }
        });
        overlapping.forEach(({ box }) => box.delete());
        return Option.wrap(ValueRegionBox.create(graph, UUID.generate(), box => {
            box.position.setValue(rangeMin);
            box.duration.setValue(rangeMax - rangeMin);
            box.loopDuration.setValue(rangeMax - rangeMin);
            box.loopOffset.setValue(0);
            box.hue.setValue(this.hue);
            box.mute.setValue(this.mute);
            box.label.setValue(this.label);
            box.events.refer(collectionBox.owners);
            box.regions.refer(trackBoxAdapter.box.regions);
        }));
    }
    toString() { return `{ValueRegionBoxAdapter ${UUID.toString(this.#box.address.uuid)} p: ${PPQN.toString(this.position)}, c: ${PPQN.toString(this.complete)}}`; }
    #dispatchChange() {
        this.#changeNotifier.notify();
        this.trackBoxAdapter.unwrapOrNull()?.regions?.dispatchChange();
    }
}
